import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';
import React from 'react';

import { CREW_LOG_ENTRY_TEXT, EMPLOYMENT_TYPE_LABELS, HOME_BASE_TYPE_LABELS } from './text';

import styles from './styles.module.css';

const formatDate = (timestamp, timezone) => {
	if (!timestamp) return null;
	return timezone
		? format(parseISO(timestamp), 'd MMM yyyy', { in: tz(timezone) })
		: format(parseISO(timestamp), 'd MMM yyyy');
};

const generateInfoUpdated = ({ newValues }) => {
	const entries = newValues
		.filter(({ value }) => value !== '' && value != null)
		.map(({ key, value }) => {
			const label = CREW_LOG_ENTRY_TEXT.personalInfoMap[key]?.text ?? key;
			return `${label}: ${value}`;
		});

	if (entries.length === 0) return null;

	return <span>{entries.join(', ')}</span>;
};

const getBaseTypeLabel = (expireTimestamp) =>
	expireTimestamp === null ? HOME_BASE_TYPE_LABELS.permanent : HOME_BASE_TYPE_LABELS.temporary;

const generateHomeBase = ({ cityName, effectiveTimestamp, expireTimestamp, timezone }) => (
	<>
		<div>{cityName}</div>
		<div className={styles.metaLine}>
			<span className={styles.summaryBadge}>{getBaseTypeLabel(expireTimestamp)}</span>
			{effectiveTimestamp && (
				<span className={styles.summaryBadge}>
					{CREW_LOG_ENTRY_TEXT.effectiveFrom} {formatDate(effectiveTimestamp, timezone)}
				</span>
			)}
		</div>
	</>
);

const generateHomeBaseDeleted = ({ cityName, expireTimestamp }) => (
	<>
		<div>{cityName}</div>
		<div className={styles.metaLine}>
			<span className={styles.summaryBadge}>{getBaseTypeLabel(expireTimestamp)}</span>
		</div>
	</>
);

const generateEmployment = ({ employmentType, effectiveTimestamp, timezone }) => {
	const label = EMPLOYMENT_TYPE_LABELS[employmentType] ?? employmentType;

	return (
		<>
			<div>{label}</div>
			{effectiveTimestamp && (
				<div className={styles.metaLine}>
					<span className={styles.summaryBadge}>
						{CREW_LOG_ENTRY_TEXT.effectiveFrom} {formatDate(effectiveTimestamp, timezone)}
					</span>
				</div>
			)}
		</>
	);
};

const generateAbsence = ({ reason, fromTimestamp, toTimestamp, timezone }) => (
	<>
		{reason && (
			<div>
				{CREW_LOG_ENTRY_TEXT.absenceType} {reason}
			</div>
		)}
		{fromTimestamp && toTimestamp && (
			<div className={styles.metaLine}>
				<span className={styles.summaryBadge}>
					{formatDate(fromTimestamp, timezone)} → {formatDate(toTimestamp, timezone)}
				</span>
			</div>
		)}
	</>
);

const generateLocationChange = ({ cityName, effectiveTimestamp, timezone }) => (
	<>
		<div>{cityName}</div>
		{effectiveTimestamp && (
			<div className={styles.metaLine}>
				<span className={styles.summaryBadge}>
					{CREW_LOG_ENTRY_TEXT.effectiveFrom} {formatDate(effectiveTimestamp, timezone)}
				</span>
			</div>
		)}
	</>
);

const generateHoldDown = ({
	originalCrewMemberId,
	originalCrewMemberName,
	effectiveTimestamp,
	expireTimestamp,
	timezone,
}) => (
	<>
		<div>
			{CREW_LOG_ENTRY_TEXT.replacingDriver} {originalCrewMemberName ?? originalCrewMemberId}
		</div>
		{effectiveTimestamp && (
			<div className={styles.metaLine}>
				<span className={styles.summaryBadge}>
					{formatDate(effectiveTimestamp, timezone)}
					{expireTimestamp && <> → {formatDate(expireTimestamp, timezone)}</>}
				</span>
			</div>
		)}
	</>
);

export const generateCrewLogEntry = (parameters, type) => {
	switch (type) {
		case 'crewMemberInfoUpdated':
			return generateInfoUpdated(parameters);
		case 'crewMemberHomeBaseCreated':
		case 'crewMemberHomeBaseUpdated':
			return generateHomeBase(parameters);
		case 'crewMemberHomeBaseDeleted':
			return generateHomeBaseDeleted(parameters);
		case 'crewMemberEmploymentCreated':
		case 'crewMemberEmploymentUpdated':
		case 'crewMemberEmploymentDeleted':
			return generateEmployment(parameters);
		case 'crewMemberAbsenceCreated':
		case 'crewMemberAbsenceUpdated':
		case 'crewMemberAbsenceDeleted':
			return generateAbsence(parameters);
		case 'crewMemberLocationChangeCreated':
		case 'crewMemberLocationChangeUpdated':
		case 'crewMemberLocationChangeDeleted':
			return generateLocationChange(parameters);
		case 'crewMemberHoldDownCreated':
		case 'crewMemberHoldDownUpdated':
		case 'crewMemberHoldDownDeleted':
			return generateHoldDown(parameters);
		default:
			return null;
	}
};
